import { SUFFIX_DOMAIN } from "@/config/constants";
import { motion } from "framer-motion";
import { HiCheckCircle, HiXCircle, HiInformationCircle } from "react-icons/hi2";
import { Button } from "rizzui";
import { useAuthHelper } from "../auth/helper";
import { EXeroPaymentStatus, IXeroPayment } from "@/utilities/types/Payment";
import { useEffect, useMemo } from "react";
import UserFlagRepository from "@/utilities/repositories/UserFlag";
import { IResponseData, IUpdateResult } from "@/utilities/types/requests";
import useApi from "@/hooks/useApi";
import { EUserFlagType } from "@/utilities/types/UserFlag";

export default function PaymentDone({
  status = EXeroPaymentStatus.PENDING,
  payment,
}: {
  status?: EXeroPaymentStatus;
  payment?: IXeroPayment | null;
}) {
  const signInHref = `https://admin${SUFFIX_DOMAIN}/sign-in`;

  const { request: deleteUserFlagByType } = useApi<
    IResponseData<IUpdateResult>
  >({
    request: UserFlagRepository.deleteUserFlagByType,
  });

  useEffect(() => {
    if (status === EXeroPaymentStatus.SUCCEED && payment) {
      deleteUserFlagByType(EUserFlagType.PAYMENT_CONFIRMED);
    }
  }, [status, payment?.userId]);

  const paymentStateByStatus = useMemo(() => {
    switch (status) {
      case EXeroPaymentStatus.SUCCEED:
        return {
          title: "Payment Succeeded",
          message: "Your payment has been successfully verified.",
          icon: <HiCheckCircle className="w-24 h-24 text-green-500" />,
        };
      case EXeroPaymentStatus.PENDING:
        return {
          title: "Payment Pending",
          message:
            "While we are verifying your payment, you may start creating your landing page.",
          icon: <HiInformationCircle className="w-24 h-24 text-blue-500" />,
        };
      case EXeroPaymentStatus.FAILED:
        return {
          title: "Payment Failed",
          message: "Your payment has failed. Please try again.",
          icon: <HiXCircle className="w-24 h-24 text-red-500" />,
        };
      default:
        return null;
    }
  }, [status]);

  const { clearAuth } = useAuthHelper();

  const navigateToSignIn = async () => {
    await clearAuth();
    window.location.replace(signInHref);
  };

  if (!paymentStateByStatus) return null;

  console.log({ status });
  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="text-center max-w-2xl mx-auto p-8"
      >
        <div className="flex justify-center mb-8">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
          >
            {paymentStateByStatus.icon}
          </motion.div>
        </div>

        <motion.h1
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="text-3xl font-bold mb-4"
        >
          {paymentStateByStatus.title}
        </motion.h1>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="mb-8"
        >
          <p className="text-gray-600 text-lg font-medium">
            {paymentStateByStatus.message}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="space-y-4"
        >
          <Button onClick={navigateToSignIn} size="lg">
            Login to Dashboard
          </Button>
        </motion.div>
      </motion.div>
    </div>
  );
}
